import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    ClipboardDocumentListIcon,
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    PaperAirplaneIcon,
    ExclamationTriangleIcon,
    UserPlusIcon
} from '@heroicons/react/24/outline';

interface TaskApplication {
    _id: string;
    status: 'invited' | 'applied' | 'approved' | 'rejected';
    appliedAt: string | null;
    reviewedAt: string | null;
    assignedDesignation?: string;
    applicationMessage?: string;
    rejectionReason?: string;
    createdAt: string;
    user?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
        region?: string;
        qualification?: string;
        experience?: string;
        designation?: string;
        skills?: Array<{
            _id: string;
            name: string;
        }>;
        personalDetails?: {
            address?: {
                state?: string;
                district?: string;
            };
        };
    };
    staff?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        role: string;
        designation?: string;
        isActive: boolean;
    };
    task: {
        _id: string;
        title: string;
        description: string;
        company: {
            companyName: string;
        };
    };
    reviewedBy?: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

const TaskApplicationManagement: React.FC = () => {
    const { id: taskId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();
    const [applications, setApplications] = useState<TaskApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<TaskApplication | null>(null);
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
    const [selectedDesignation, setSelectedDesignation] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    // Filter states
    const [designationFilter, setDesignationFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');
    const [experienceFilter, setExperienceFilter] = useState('all');
    const [activeTab, setActiveTab] = useState<'users' | 'applied_users' | 'staff'>('users');

    // Manual assignment states
    const [showManualAssignmentModal, setShowManualAssignmentModal] = useState(false);
    const [manualAssignmentForm, setManualAssignmentForm] = useState({
        userId: '',
        staffIds: [] as string[],
        designation: '',
        message: ''
    });
    const [availableUsers, setAvailableUsers] = useState<any[]>([]);
    const [availableStaff, setAvailableStaff] = useState<any[]>([]);
    const [task, setTask] = useState<any>(null);
    const [assignmentType, setAssignmentType] = useState<'user' | 'staff'>('user');

    const fetchApplications = useCallback(async () => {
        if (!taskId) return;

        try {
            setLoading(true);
            const response = await api.get('/admin/task-applications', {
                params: {
                    taskId,
                    status: undefined, // Remove status filtering since we're using tabs for user/staff separation
                    page: currentPage,
                    limit: 10
                }
            });

            if (response.data.success && response.data.data) {
                const { applications: apps, pagination } = response.data.data;
                setApplications(apps || []);
                setTotalPages(pagination?.totalPages || 1);
                setTotalItems(pagination?.totalItems || 0);
            } else {
                setApplications([]);
                setTotalPages(1);
                setTotalItems(0);
            }
        } catch (error: any) {
            console.error('Error fetching applications:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch applications',
                type: 'error'
            }));
            setApplications([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [taskId, activeTab, currentPage, dispatch]);

    const fetchAvailableUsers = useCallback(async (designation: string) => {
        if (!designation) {
            setAvailableUsers([]);
            return;
        }

        try {
            const response = await api.get('/admin/users', {
                params: {
                    role: 'user',
                    designation: designation,
                    limit: 1000 // Get all users for manual assignment
                }
            });

            if (response.data.success) {
                const users = response.data.data || [];

                // Filter users based on task skills if task data is available
                const filteredUsers = users.filter((user: any) => {
                    if (!task?.requiredSkills || task.requiredSkills.length === 0) {
                        return true; // No skill requirements, show all users with matching designation
                    }

                    const userSkills = user.skills?.map((skill: any) => skill.name) || [];

                    // Extract skill names from task requiredSkills (they are populated objects)
                    const taskSkillNames = task.requiredSkills?.map((skill: any) => skill.name) || [];

                    // Check if user has at least one matching skill with task requirements
                    const hasMatchingSkill = taskSkillNames.some((requiredSkillName: string) =>
                        userSkills.includes(requiredSkillName)
                    );

                    return hasMatchingSkill;
                });

                setAvailableUsers(filteredUsers);
            }
        } catch (error: any) {
            console.error('Error fetching users:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch users',
                type: 'error'
            }));
        }
    }, [dispatch, task]);

    const fetchAvailableStaff = useCallback(async () => {
        try {
            const response = await api.get('/admin/staff', {
                params: {
                    limit: 1000 // Get all staff for manual assignment
                }
            });

            if (response.data.success) {
                setAvailableStaff(response.data.data || []);
            }
        } catch (error: any) {
            console.error('Error fetching staff:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch staff',
                type: 'error'
            }));
        }
    }, [dispatch]);

    // Filter staff based on selected designation
    const filteredStaff = useMemo(() => {
        if (!manualAssignmentForm.designation || assignmentType !== 'staff') {
            return availableStaff.filter(staff => staff && staff._id);
        }
        return availableStaff.filter(staff => staff && staff._id && staff.role === manualAssignmentForm.designation);
    }, [availableStaff, manualAssignmentForm.designation, assignmentType]);

    const fetchTaskDetails = useCallback(async () => {
        if (!taskId) return;

        try {
            const response = await api.get(`/admin/tasks/${taskId}`);
            if (response.data.success) {
                setTask(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching task details:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch task details',
                type: 'error'
            }));
        }
    }, [taskId, dispatch]);

    useEffect(() => {
        if (taskId) {
            fetchApplications();
            fetchTaskDetails();
        }
    }, [taskId, fetchApplications, fetchTaskDetails]);

    const handleReviewApplication = async () => {
        if (!selectedApplication) return;

        try {
            const response = await api.put(`/admin/task-applications/${selectedApplication._id}/review`, {
                action: reviewAction,
                designation: reviewAction === 'approve' ? selectedDesignation : undefined,
                rejectionReason: reviewAction === 'reject' ? rejectionReason : undefined
            });

            dispatch(showToast({
                message: response.data.message,
                type: 'success'
            }));

            setShowReviewModal(false);
            setSelectedApplication(null);
            setReviewAction('approve');
            setSelectedDesignation('');
            setRejectionReason('');
            fetchApplications();
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || `Failed to ${reviewAction} application`,
                type: 'error'
            }));
        }
    };

    const openReviewModal = (application: TaskApplication, action: 'approve' | 'reject') => {
        setSelectedApplication(application);
        setReviewAction(action);
        // Auto-set designation from user profile for approval
        setSelectedDesignation(action === 'approve' ? (application.user?.designation || '') : '');
        setRejectionReason('');
        setShowReviewModal(true);
    };

    const handleManualAssignmentSubmit = async () => {
        const isUserAssignment = assignmentType === 'user';
        const idValue = isUserAssignment ? manualAssignmentForm.userId : manualAssignmentForm.staffIds;

        if (!manualAssignmentForm.designation) {
            dispatch(showToast({
                message: 'Please select a designation',
                type: 'error'
            }));
            return;
        }

        if (isUserAssignment) {
            if (!manualAssignmentForm.userId) {
                dispatch(showToast({
                    message: 'Please select a user',
                    type: 'error'
                }));
                return;
            }
        } else {
            if (manualAssignmentForm.staffIds.length === 0) {
                dispatch(showToast({
                    message: 'Please select at least one staff member',
                    type: 'error'
                }));
                return;
            }
        }

        try {
            if (isUserAssignment) {
                // Single user assignment
                const response = await api.post('/admin/task-applications/manual-assignment', {
                    taskId,
                    userId: manualAssignmentForm.userId,
                    assignmentType,
                    designation: manualAssignmentForm.designation,
                    message: manualAssignmentForm.message || `Manually assigned ${assignmentType} by admin`
                });

                if (response.data.success) {
                    dispatch(showToast({
                        message: 'User assigned to task successfully',
                        type: 'success'
                    }));
                }
            } else {
                // Multiple staff assignment
                const assignmentPromises = manualAssignmentForm.staffIds.map(staffId =>
                    api.post('/admin/task-applications/manual-assignment', {
                        taskId,
                        staffId,
                        assignmentType,
                        designation: manualAssignmentForm.designation,
                        message: manualAssignmentForm.message || `Manually assigned ${assignmentType} by admin`
                    })
                );

                const responses = await Promise.all(assignmentPromises);
                const successCount = responses.filter(response => response.data.success).length;

                dispatch(showToast({
                    message: `${successCount} staff member(s) assigned to task successfully`,
                    type: 'success'
                }));
            }

            setShowManualAssignmentModal(false);
            setManualAssignmentForm({
                userId: '',
                staffIds: [],
                designation: '',
                message: ''
            });
            setAssignmentType('user');
            fetchApplications();
        } catch (error: any) {
            console.error(`Error assigning ${assignmentType}:`, error);
            dispatch(showToast({
                message: error.response?.data?.message || `Failed to assign ${assignmentType}`,
                type: 'error'
            }));
        }
    };

    const openManualAssignmentModal = () => {
        setShowManualAssignmentModal(true);
        setManualAssignmentForm({
            userId: '',
            staffIds: [],
            designation: '',
            message: ''
        });
        setAssignmentType('user');
        setAvailableUsers([]);
        setAvailableStaff([]);
        fetchAvailableStaff();
    };

    const handleDesignationChange = (designation: string) => {
        setManualAssignmentForm(prev => ({
            ...prev,
            designation,
            userId: '', // Reset user selection when designation changes
            staffIds: [] // Reset staff selection when designation changes
        }));

        if (assignmentType === 'user') {
            fetchAvailableUsers(designation);
        }
        // For staff, we'll filter the already fetched staff based on designation
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'invited':
                return <ClockIcon className="h-5 w-5 text-yellow-500" />;
            case 'applied':
                return <PaperAirplaneIcon className="h-5 w-5 text-blue-500" />;
            case 'approved':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case 'rejected':
                return <XCircleIcon className="h-5 w-5 text-red-500" />;
            default:
                return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'invited':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'applied':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'invited':
                return 'Invited';
            case 'applied':
                return 'Applied';
            case 'approved':
                return 'Approved';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Unknown';
        }
    };

    // Filter applications based on selected filters and tab
    const filteredApplications = applications.filter(application => {
        // Separate user and staff applications
        const isUserApplication = application.user && !application.staff;
        const isStaffApplication = application.staff && !application.user;

        // Filter by tab
        if (activeTab === 'users' && !isUserApplication) return false;
        if (activeTab === 'applied_users' && (!isUserApplication || application.status !== 'applied')) return false;
        if (activeTab === 'staff' && !isStaffApplication) return false;

        // Apply filters based on application type
        if (isUserApplication) {
            const matchesDesignation = designationFilter === 'all' || application.user?.designation === designationFilter;
            const matchesRegion = regionFilter === 'all' || application.user?.region === regionFilter;
            const matchesExperience = experienceFilter === 'all' || application.user?.experience === experienceFilter;
            return matchesDesignation && matchesRegion && matchesExperience;
        } else if (isStaffApplication) {
            const matchesDesignation = designationFilter === 'all' || application.staff?.role === designationFilter;
            // Staff don't have region/experience filters, so always match
            return matchesDesignation;
        }

        return false;
    });

    if (loading) {
        return (
            <div className="space-y-6 container mx-auto px-4 py-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 dark:text-gray-400">Loading applications...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 container mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Task Applications
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Review and manage task applications
                        </p>
                    </div>
                    <UserGroupIcon className="h-8 w-8 text-blue-600" />
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {[
                            { key: 'users', label: `All Users (${applications.filter(app => app.user && !app.staff).length})` },
                            { key: 'applied_users', label: `Applied Users (${applications.filter(app => app.user && !app.staff && app.status === 'applied').length})` },
                            { key: 'staff', label: `Staff (${applications.filter(app => app.staff && !app.user).length})` }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => {
                                    setActiveTab(tab.key as 'users' | 'applied_users' | 'staff');
                                    setCurrentPage(1);
                                }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Filters */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Designation Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Filter by Designation
                            </label>
                            <select
                                value={designationFilter}
                                onChange={(e) => setDesignationFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Designations</option>
                                {(activeTab === 'users' || activeTab === 'applied_users') ? (
                                    <>
                                        <option value="team_executive">Team Executive</option>
                                        <option value="team_leader">Team Leader</option>
                                        <option value="district_head">District Head</option>
                                        <option value="state_head">State Head</option>
                                        <option value="regional_head">Regional Head</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="bdm">BDM</option>
                                        <option value="project_trainer">Project Trainer</option>
                                        <option value="recruiter">Recruiter</option>
                                        <option value="crm">CRM</option>
                                    </>
                                )}
                            </select>
                        </div>

                        {/* Region Filter - Only for Users */}
                        {(activeTab === 'users' || activeTab === 'applied_users') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Filter by Region
                                </label>
                                <select
                                    value={regionFilter}
                                    onChange={(e) => setRegionFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Regions</option>
                                    <option value="north">North</option>
                                    <option value="south">South</option>
                                    <option value="east">East</option>
                                    <option value="west">West</option>
                                    <option value="central">Central</option>
                                    <option value="northeast">Northeast</option>
                                </select>
                            </div>
                        )}

                        {/* Experience Filter - Only for Users */}
                        {(activeTab === 'users' || activeTab === 'applied_users') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Filter by Experience
                                </label>
                                <select
                                    value={experienceFilter}
                                    onChange={(e) => setExperienceFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Experience Levels</option>
                                    <option value="fresher">Fresher</option>
                                    <option value="junior">Junior</option>
                                    <option value="mid-level">Mid-level</option>
                                    <option value="senior">Senior</option>
                                    <option value="expert">Expert</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Manual Assignment Button */}
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={openManualAssignmentModal}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                            <UserPlusIcon className="h-4 w-4" />
                            Manual Assignment
                        </button>
                    </div>
                </div>

                {/* Applications List */}
                <div className="p-6">
                    {filteredApplications.length === 0 ? (
                        <div className="text-center py-12">
                            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                No applications found
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-4">
                                {applications.length === 0
                                    ? `No ${activeTab} applications have been submitted for this task yet.`
                                    : `No ${activeTab} applications match the current filters. Try adjusting your filter criteria.`
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredApplications.map((application) => (
                                <div
                                    key={application._id}
                                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {(activeTab === 'users' || activeTab === 'applied_users')
                                                        ? `${application.user?.firstName || 'N/A'} ${application.user?.lastName || 'N/A'}`
                                                        : `${application.staff?.firstName || 'N/A'} ${application.staff?.lastName || 'N/A'}`
                                                    }
                                                </h3>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                                    {getStatusText(application.status)}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Email:</strong> {(activeTab === 'users' || activeTab === 'applied_users') ? (application.user?.email || 'N/A') : (application.staff?.email || 'N/A')}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Phone:</strong> {(activeTab === 'users' || activeTab === 'applied_users') ? (application.user?.phone || 'Not provided') : (application.staff?.phone || 'Not provided')}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>{(activeTab === 'users' || activeTab === 'applied_users') ? 'Region:' : 'Role:'}</strong> {(activeTab === 'users' || activeTab === 'applied_users') ? (application.user?.region || 'Not specified') : (application.staff?.role?.replace('_', ' ').toUpperCase() || 'Not specified')}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>{(activeTab === 'users' || activeTab === 'applied_users') ? 'Skills:' : 'Designation:'}</strong> {(activeTab === 'users' || activeTab === 'applied_users') ? (application.user?.skills?.map((skill: any) => skill.name).join(', ') || 'Not specified') : (application.staff?.designation || 'Not specified')}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>{(activeTab === 'users' || activeTab === 'applied_users') ? 'State:' : 'Status:'}</strong> {(activeTab === 'users' || activeTab === 'applied_users') ? (application.user?.personalDetails?.address?.state || 'Not specified') : (application.staff?.isActive ? 'Active' : 'Inactive')}
                                                    </p>
                                                    {(activeTab === 'users' || activeTab === 'applied_users') && (
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>District:</strong> {application.user?.personalDetails?.address?.district || 'Not specified'}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    {(activeTab === 'users' || activeTab === 'applied_users') ? (
                                                        <>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Qualification:</strong> {application.user?.qualification || 'Not specified'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Experience:</strong> {application.user?.experience || 'Not specified'}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                <strong>Profile Designation:</strong> {application.user?.designation ? application.user.designation.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Not specified'}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {/* Staff-specific fields can be added here */}
                                                        </div>
                                                    )}

                                                    {application.assignedDesignation && (
                                                        <p className="text-sm text-green-600 dark:text-green-400">
                                                            <strong>Assigned for Task:</strong> {application.assignedDesignation.replace('_', ' ')}
                                                        </p>
                                                    )}
                                                    {!application.assignedDesignation && application.status === 'applied' && (
                                                        <p className="text-sm text-orange-600 dark:text-orange-400">
                                                            <strong>Task Designation:</strong> Pending assignment
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {application.applicationMessage && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Application Message:</strong>
                                                    </p>
                                                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                                                        {application.applicationMessage}
                                                    </p>
                                                </div>
                                            )}

                                            {application.assignedDesignation && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-green-600 dark:text-green-400">
                                                        <strong>Assigned Designation:</strong> {application.assignedDesignation.replace('_', ' ')}
                                                    </p>
                                                </div>
                                            )}

                                            {application.rejectionReason && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-red-600 dark:text-red-400">
                                                        <strong>Rejection Reason:</strong>
                                                    </p>
                                                    <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                                                        {application.rejectionReason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 ml-4">
                                            {getStatusIcon(application.status)}

                                            {application.status === 'applied' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => openReviewModal(application, 'approve')}
                                                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    >
                                                        <CheckCircleIcon className="h-4 w-4" />
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => openReviewModal(application, 'reject')}
                                                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm font-medium"
                                                    >
                                                        <XCircleIcon className="h-4 w-4" />
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                Showing page {currentPage} of {totalPages} ({totalItems} total applications)
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Review Modal */}
            {showReviewModal && selectedApplication && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                {reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}
                            </h3>

                            {/* User/Staff Details Section */}
                            <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                    <UserGroupIcon className="h-5 w-5 mr-2" />
                                    {selectedApplication.user ? 'User Details' : 'Staff Details'}
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Name:</strong> {selectedApplication.user ? `${selectedApplication.user?.firstName || 'N/A'} ${selectedApplication.user?.lastName || 'N/A'}` : `${selectedApplication.staff?.firstName || 'N/A'} ${selectedApplication.staff?.lastName || 'N/A'}`}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Email:</strong> {selectedApplication.user ? (selectedApplication.user?.email || 'N/A') : (selectedApplication.staff?.email || 'N/A')}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Phone:</strong> {selectedApplication.user ? (selectedApplication.user?.phone || 'Not provided') : (selectedApplication.staff?.phone || 'Not provided')}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>{selectedApplication.user ? 'Region:' : 'Role:'}</strong> {selectedApplication.user ? (selectedApplication.user?.region || 'Not specified') : (selectedApplication.staff?.role?.replace('_', ' ').toUpperCase() || 'Not specified')}
                                        </p>
                                    </div>
                                    <div>
                                        {selectedApplication.user ? (
                                            <>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Skills:</strong> {selectedApplication.user?.skills?.map((skill: any) => skill.name).join(', ') || 'Not specified'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Qualification:</strong> {selectedApplication.user?.qualification || 'Not specified'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Experience:</strong> {selectedApplication.user?.experience || 'Not specified'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Profile Designation:</strong> {selectedApplication.user?.designation ? selectedApplication.user.designation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not specified'}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Designation:</strong> {selectedApplication.staff?.designation || 'Not specified'}
                                                </p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    <strong>Status:</strong> {selectedApplication.staff?.isActive ? 'Active' : 'Inactive'}
                                                </p>
                                            </>
                                        )}

                                        {selectedApplication.assignedDesignation && (
                                            <p className="text-sm text-green-600 dark:text-green-400">
                                                <strong>Assigned for Task:</strong> {selectedApplication.assignedDesignation.replace('_', ' ')}
                                            </p>
                                        )}
                                        {!selectedApplication.assignedDesignation && selectedApplication.status === 'applied' && (
                                            <p className="text-sm text-orange-600 dark:text-orange-400">
                                                <strong>Task Designation:</strong> Pending assignment
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {selectedApplication.user && (
                                    <div className="mt-3">
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>Location:</strong> {selectedApplication.user?.personalDetails?.address?.state || 'Not specified'}, {selectedApplication.user?.personalDetails?.address?.district || 'Not specified'}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Task Details Section */}
                            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                                    Task Details
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Task:</strong> {selectedApplication.task.title}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Company:</strong> {selectedApplication.task.company.companyName}
                                </p>
                            </div>

                            {reviewAction === 'approve' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task Designation
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                        Designation automatically set from user profile. This ensures consistency across all tasks.
                                    </p>
                                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white">
                                        {selectedDesignation ? selectedDesignation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'No designation'}
                                    </div>
                                </div>
                            )}

                            {reviewAction === 'reject' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Rejection Reason (Optional)
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Provide a reason for rejection..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        rows={3}
                                    />
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowReviewModal(false);
                                        setSelectedApplication(null);
                                        setReviewAction('approve');
                                        setSelectedDesignation('');
                                        setRejectionReason('');
                                    }}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReviewApplication}
                                    disabled={reviewAction === 'approve' && !selectedDesignation}
                                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${reviewAction === 'approve'
                                        ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                >
                                    {reviewAction === 'approve' ? (
                                        <>
                                            <CheckCircleIcon className="h-4 w-4" />
                                            Approve & Assign Designation
                                        </>
                                    ) : (
                                        <>
                                            <XCircleIcon className="h-4 w-4" />
                                            Reject Application
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Assignment Modal */}
            {showManualAssignmentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Manual Assignment
                            </h3>

                            <div className="space-y-4">
                                {/* Assignment Type Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assignment Type *
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                value="user"
                                                checked={assignmentType === 'user'}
                                                onChange={(e) => {
                                                    setAssignmentType('user');
                                                    setManualAssignmentForm(prev => ({ ...prev, userId: '', staffIds: [] }));
                                                }}
                                                className="mr-2"
                                            />
                                            User
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                value="staff"
                                                checked={assignmentType === 'staff'}
                                                onChange={(e) => {
                                                    setAssignmentType('staff');
                                                    setManualAssignmentForm(prev => ({ ...prev, userId: '', staffIds: [] }));
                                                }}
                                                className="mr-2"
                                            />
                                            Staff
                                        </label>
                                    </div>
                                </div>

                                {/* Task Skills Info */}
                                {task?.requiredSkills && task.requiredSkills.length > 0 && assignmentType === 'user' && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                                            Task Required Skills:
                                        </p>
                                        <p className="text-sm text-blue-700 dark:text-blue-300">
                                            {task.requiredSkills?.map((skill: any) => skill.name).join(', ')}
                                        </p>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                            Only users with matching skills will be shown
                                        </p>
                                    </div>
                                )}

                                {/* Designation Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Task Designation *
                                    </label>
                                    <select
                                        value={manualAssignmentForm.designation}
                                        onChange={(e) => handleDesignationChange(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select designation...</option>
                                        {assignmentType === 'user' ? (
                                            <>
                                                <option value="team_executive">Team Executive</option>
                                                <option value="team_leader">Team Leader</option>
                                                <option value="district_head">District Head</option>
                                                <option value="state_head">State Head</option>
                                                <option value="regional_head">Regional Head</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="bdm">BDM (Business Development Manager)</option>
                                                <option value="project_trainer">Project Trainer</option>
                                                <option value="recruiter">Recruiter</option>
                                                <option value="crm">CRM (Customer Relationship Manager)</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                {/* User/Staff Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Select {assignmentType === 'user' ? 'User' : 'Staff'} *
                                    </label>
                                    {assignmentType === 'user' ? (
                                        <select
                                            value={manualAssignmentForm.userId}
                                            onChange={(e) => setManualAssignmentForm(prev => ({ ...prev, userId: e.target.value }))}
                                            disabled={!manualAssignmentForm.designation}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                                        >
                                            <option value="">
                                                {!manualAssignmentForm.designation
                                                    ? "Select designation first..."
                                                    : availableUsers.length === 0
                                                        ? "No matching users found"
                                                        : "Select a user..."
                                                }
                                            </option>
                                            {availableUsers.map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.firstName} {user.lastName} ({user.email})
                                                    {user.skills && user.skills.length > 0 && (
                                                        <span className="text-gray-500">
                                                            - Skills: {user.skills.map((skill: any) => skill.name).join(', ')}
                                                        </span>
                                                    )}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="space-y-2">
                                            {filteredStaff.length > 0 && (
                                                <div className="flex items-center p-2 bg-gray-50 dark:bg-gray-600 rounded-md">
                                                    <input
                                                        type="checkbox"
                                                        checked={manualAssignmentForm.staffIds.length === filteredStaff.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setManualAssignmentForm(prev => ({
                                                                    ...prev,
                                                                    staffIds: filteredStaff.filter(staff => staff && staff._id).map(staff => staff._id)
                                                                }));
                                                            } else {
                                                                setManualAssignmentForm(prev => ({
                                                                    ...prev,
                                                                    staffIds: []
                                                                }));
                                                            }
                                                        }}
                                                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Select All ({filteredStaff.length} staff)
                                                    </span>
                                                </div>
                                            )}
                                            <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                                                {filteredStaff.length === 0 ? (
                                                    <div className="p-3 text-gray-500 dark:text-gray-400 text-sm">
                                                        {manualAssignmentForm.designation
                                                            ? `No ${manualAssignmentForm.designation.replace('_', ' ')} staff found`
                                                            : 'No staff found'
                                                        }
                                                    </div>
                                                ) : (
                                                    filteredStaff.filter(staff => staff && staff._id).map(staff => (
                                                        <label key={staff._id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={manualAssignmentForm.staffIds.includes(staff._id)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setManualAssignmentForm(prev => ({
                                                                            ...prev,
                                                                            staffIds: [...prev.staffIds, staff._id]
                                                                        }));
                                                                    } else {
                                                                        setManualAssignmentForm(prev => ({
                                                                            ...prev,
                                                                            staffIds: prev.staffIds.filter(id => id !== staff._id)
                                                                        }));
                                                                    }
                                                                }}
                                                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {staff.firstName || 'N/A'} {staff.lastName || 'N/A'}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {staff.email || 'N/A'} - {staff.role?.replace('_', ' ').toUpperCase() || 'N/A'}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    ))
                                                )}
                                            </div>
                                            {manualAssignmentForm.staffIds.length > 0 && (
                                                <div className="text-sm text-blue-600 dark:text-blue-400">
                                                    {manualAssignmentForm.staffIds.length} staff member(s) selected
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {manualAssignmentForm.designation && (
                                        assignmentType === 'user' ? (
                                            availableUsers.length === 0 && (
                                                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                                    No users found with matching designation and skills
                                                </p>
                                            )
                                        ) : (
                                            availableStaff.length === 0 && (
                                                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                                    No staff found
                                                </p>
                                            )
                                        )
                                    )}
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Assignment Message (Optional)
                                    </label>
                                    <textarea
                                        value={manualAssignmentForm.message}
                                        onChange={(e) => setManualAssignmentForm(prev => ({ ...prev, message: e.target.value }))}
                                        placeholder="Add a message for the user..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        rows={3}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowManualAssignmentModal(false);
                                        setManualAssignmentForm({
                                            userId: '',
                                            staffIds: [],
                                            designation: '',
                                            message: ''
                                        });
                                        setAssignmentType('user');
                                    }}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleManualAssignmentSubmit}
                                    disabled={
                                        !manualAssignmentForm.designation ||
                                        (assignmentType === 'user' ? !manualAssignmentForm.userId : manualAssignmentForm.staffIds.length === 0)
                                    }
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                >
                                    <UserPlusIcon className="h-4 w-4" />
                                    Assign {assignmentType === 'user' ? 'User' : `${manualAssignmentForm.staffIds.length} Staff`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskApplicationManagement;
